import React, { FunctionComponent, ChangeEvent } from 'react';
import styled from 'styled-components';
import { ColorChangeHandler, ColorState } from 'react-color';
import { Grid, Checkbox, Colors, Input, Tooltip, Switch } from '@inplayer-org/inplayer-ui';
import { MdInfoOutline } from 'react-icons/md';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// constants
import Rules from 'restrictions/roles/constants/rules';

// components
import UploadFile from 'components/UploadFile';
import { BlueTypography } from 'components/SharedStyledElements';
import { Branding } from './Preview';
import ColorPicker from './ColorPicker';
import PaywallConfigurationContent from './PaywallConfigurationContent';
import PaywallModalColorsConfigurationContent from './PaywallModalColorsConfigurationContent';
import PaywallButtonConfigurationContent from './PaywallButtonConfigurationContent';
import PaywallModalButtonConfigurationContent from './PaywallModalButtonConfigurationContent';

const { Container, Cell } = Grid;

const ConfigurationContainer = styled(Container)`
  margin: 2rem;
`;

const ConfigurationWrapper = styled(Cell)`
  border-top: 1px solid ${Colors.gray};
`;

const LogoSettingsWrapper = styled(Container)`
  align-items: center;
  margin-bottom: 1rem;
`;

const TooltipWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
`;

const Wrapper = styled.div`
  margin: 1rem 0;
`;

interface OwnProps {
  title: string;
  resetForm: () => any;
  uploadImage: (file: File) => any;
  configTitle: string;
  branding: Branding;
  handleChange: (input: string, checked: string | boolean) => any;
  handleClick: (input: string) => any;
  handleClose: (input: string) => any;
  isLogoShown?: boolean;
  handleShowLogoToggle?: (e: any) => any;
  showLogoConfig?: boolean;
  isTermsFeatureEnabled?: boolean;
  previewTemplateId?: number;
}

const PaywallConfigurationFrom: FunctionComponent<OwnProps> = ({
  title,
  resetForm,
  uploadImage,
  configTitle,
  branding: {
    // Paywall V1
    inplayer_protected_label,
    displayBtnColorPicker,
    preview_buttons_bg_color,
    displayTextColorPicker,
    preview_buttons_text_color,
    // Paywall V2/v3
    paywall_primary_color,
    paywall_secondary_color,
    displaySecondaryColorPicker,
    displayPrimaryColorPicker,
    paywall_buttons_bg_color,
    displayModalBtnColorPicker,
    terms_url,
    dark_mode,
  },
  isLogoShown,
  handleShowLogoToggle,
  handleChange,
  handleClick,
  handleClose,
  showLogoConfig,
  isTermsFeatureEnabled,
  previewTemplateId,
}) => {
  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);

  const handleCheckboxChange = (elementName: string, checked: boolean) => () => {
    handleChange(elementName, checked);
  };

  const handlePickerChange = (input: string): ColorChangeHandler => () => {
    return (color: ColorState) => {
      handleChange(input, color.hex);
    };
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const {
      target: { value },
    } = e;

    handleChange('terms_url', value);
  };

  return (
    <ConfigurationContainer columns={1}>
      <Cell>
        <BlueTypography variant="h6">{title}</BlueTypography>
      </Cell>
      <Cell>
        <UploadFile resetForm={resetForm} uploadImage={uploadImage} disabled={isReadOnly} />
      </Cell>
      {showLogoConfig && (
        <Cell>
          <LogoSettingsWrapper columns={2}>
            <Cell>
              <ColorPicker
                tag="logo_background_color_picker"
                showColorPicker={displaySecondaryColorPicker}
                displayPicker="displaySecondaryColorPicker"
                element_name="paywall_secondary_color"
                background_color={paywall_secondary_color!}
                text="Logo background color"
                handleClick={handleClick('displaySecondaryColorPicker')}
                handleClose={handleClose('displaySecondaryColorPicker')}
                handlePickerChange={handlePickerChange('paywall_secondary_color')}
              />
            </Cell>
            <Cell>
              <Checkbox
                tag="checkbox_show_logo"
                checked={isLogoShown!}
                name="checkbox"
                id="checkbox"
                label="Show logo"
                onChange={handleShowLogoToggle!(!isLogoShown)}
                disabled={isReadOnly}
              />
            </Cell>
          </LogoSettingsWrapper>
        </Cell>
      )}
      <ConfigurationWrapper top={4}>
        <Container columns={2}>
          <Cell top={2}>
            <BlueTypography variant="h6">{configTitle} configuration</BlueTypography>
            {configTitle === 'Elements' && (
              <Wrapper>
                <PaywallConfigurationContent
                  inplayer_protected_label={inplayer_protected_label!}
                  handleCheckboxChange={handleCheckboxChange}
                />
              </Wrapper>
            )}
            {configTitle === 'Paywall' && (
              <Wrapper>
                <PaywallModalColorsConfigurationContent
                  displayPrimaryColorPicker={displayPrimaryColorPicker!}
                  paywall_primary_color={paywall_primary_color!}
                  handleClick={handleClick}
                  handleClose={handleClose}
                  handlePickerChange={handlePickerChange}
                />
              </Wrapper>
            )}
          </Cell>
          <Cell top={2}>
            <BlueTypography variant="h6">Button configuration</BlueTypography>
            {configTitle === 'Elements' && (
              <Wrapper>
                <PaywallButtonConfigurationContent
                  displayBtnColorPicker={displayBtnColorPicker!}
                  preview_buttons_bg_color={preview_buttons_bg_color!}
                  displayTextColorPicker={displayTextColorPicker!}
                  preview_buttons_text_color={preview_buttons_text_color!}
                  handleClick={handleClick}
                  handleClose={handleClose}
                  previewTemplateId={previewTemplateId}
                  handlePickerChange={handlePickerChange}
                />
              </Wrapper>
            )}
            {configTitle === 'Paywall' && (
              <Wrapper>
                <PaywallModalButtonConfigurationContent
                  displayModalBtnColorPicker={displayModalBtnColorPicker!}
                  paywall_buttons_bg_color={paywall_buttons_bg_color!}
                  handleClick={handleClick}
                  handleClose={handleClose}
                  handlePickerChange={handlePickerChange}
                />
              </Wrapper>
            )}
          </Cell>
        </Container>
      </ConfigurationWrapper>
      {showLogoConfig && isTermsFeatureEnabled && (
        <ConfigurationWrapper top={6}>
          <Container columns={1}>
            <Cell top={2}>
              <BlueTypography variant="h6">Terms and Conditions link</BlueTypography>
              <TooltipWrapper>
                <Tooltip
                  tag="tooltip_terms_and_conditions_link"
                  placement="right"
                  content={`If no terms and conditions link is specified,\nthe default one (https://inplayer.com/terms/)\nwill be applied.`}
                >
                  <MdInfoOutline />
                </Tooltip>
              </TooltipWrapper>
              <Input
                tag="text_terms_and_conditions_link"
                type="text"
                placeholder="URL (e.g. https://yourterms&conditions.com)"
                onChange={handleInputChange}
                value={terms_url}
              />
            </Cell>
          </Container>
        </ConfigurationWrapper>
      )}
      {showLogoConfig && (
        <ConfigurationWrapper top={8}>
          <Container columns={1}>
            <Cell top={2}>
              <Switch
                tag="toggle_enable_dark_mode"
                id="darkMode"
                label="Enable dark mode"
                checked={dark_mode!!}
                onChange={handleClick('dark_mode')}
              />
            </Cell>
          </Container>
        </ConfigurationWrapper>
      )}
    </ConfigurationContainer>
  );
};

export default PaywallConfigurationFrom;
